import React, { useState, useEffect, useMemo } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import moment from 'moment-timezone';
import ClinicUtils from 'services/ClinicUtils';
import View from './view';
import AppointmentUtils from 'services/AppointmentUtils';
import CalendarUtils from 'services/CalendarUtils';
import StaffUtils from 'services/StaffUtils';
import { useAppSelector } from 'careand-redux/store';
import { api } from 'careand-redux/api/ApiThunks';
import { useAppDispatch } from 'careand-redux/useAppDispatch';
import { isResponseError } from 'careand-redux/api/SerializedReponseError';
import _ from 'lodash';

const APPOINTMENT_TYPES = AppointmentUtils.getAppointmentTypes();

const appointmentSchema = z.object({
  option_id: z.any().optional(),
  address: z.any().optional(),
  complaints: z.string().optional().or(z.literal('')),
  date: z.any().optional(),
  time: z.any().optional(),
  type: z.string().optional().or(z.literal('')),
  patient_id: z.any().optional(),
  concern_id: z.any().optional(),
  clinic_id: z.any().optional(),
  telemedicine_method: z.any().optional(),
  notes: z.string().optional().or(z.literal('')),
  selectedPhysician: z.any().optional(),
  confirmationMessage: z.any().optional(),
  booked_by: z.any().optional(),
});

type AppointmentFormValues = z.infer<typeof appointmentSchema>;

interface NewAppointmentProps {
  patient?: any;
  startTime?: number;
  physician?: any;
  clinicId?: string;
  bookedBy?: any;
  onClose: () => void;
  sendMessage?: (message: string) => void;
  allowClose?: boolean;
  prefillAddress?: any;
  appointment?: any;
}

const NewAppointment = (props: NewAppointmentProps) => {
  const dispatch = useAppDispatch();

  const authentication = useAppSelector((state) => state.authentication);
  const patients = useAppSelector((state) => state.patients.byId);
  const allPhysicians = useAppSelector((state) => state.physicians.byId);
  const clinic = useAppSelector((state) => state.resource.clinic);
  const shifts = useAppSelector((state) => state.shifts.shifts);

  const _options = useAppSelector((state) => state.appointments.options);
  const options = useMemo(() => {
    return AppointmentUtils.filterBookingOptionsWithChildren(_options);
  }, [_options]);

  const { prefillAddress, physician, startTime } = props;

  const clinics = useAppSelector((state) => state.resource.clinics);

  // Practitioner and patient profiles can still name a closed clinic; book at
  // its replacement instead.
  const initialClinicId = ClinicUtils.resolveForwardedClinicId(
    props.clinicId || props.physician?.clinic_id || props.patient?.clinic_id,
    clinics,
  );

  const { control, handleSubmit, setValue, watch, reset, getValues } =
    useForm<AppointmentFormValues>({
      resolver: zodResolver(appointmentSchema),
      defaultValues: {
        type: 'Clinic',
        clinic_id: initialClinicId,
        option_id: undefined,
        address: undefined,
        complaints: '',
        date: undefined,
        time: undefined,
        patient_id: undefined,
        concern_id: undefined,
        telemedicine_method: undefined,
        notes: '',
        selectedPhysician: undefined,
        confirmationMessage: undefined,
        booked_by: undefined,
      },
    });

  const option_id = watch('option_id');
  const address = watch('address');
  const complaints = watch('complaints');
  const date = watch('date');
  const time = watch('time');
  const type = watch('type');
  const telemedicine_method = watch('telemedicine_method');
  const concern_id = watch('concern_id');
  const clinic_id = watch('clinic_id');
  const notes = watch('notes');

  const [bookTimeOff, setBookTimeOff] = useState(false);
  const [isSubmitting, setSubmitting] = useState(false);
  const [isCalendarVisible, setCalendarVisible] = useState(true);
  const [confirmationDialog, showConfirmationDialog] = useState<string | undefined>(undefined);
  const [selectedPhysician, selectPhysician] = useState<string | undefined>(physician?.id);

  const [patientId, setPatientId] = useState(props.patient?.id || undefined);

  const [confirmPayment, setConfirmPayment] = useState(false);
  const [confirmSlot, setConfirmSlot] = useState(false);
  const [confirmType, setConfirmType] = useState<string | undefined>();

  let momentDate = useMemo(() => {
    return date ? moment.unix(date) : moment();
  }, [date]);

  function clearField(field: keyof AppointmentFormValues) {
    setValue(field, null);
  }

  let patient: any = undefined;
  if (patientId && patients[patientId]) patient = patients[patientId];

  const fixedPatient = !!props.patient;

  useEffect(() => {
    if (startTime) setTimeAndDate(moment.unix(startTime));
  }, [startTime]);

  useEffect(() => {
    if (!patient || (patientId && patientId !== patient.id))
      dispatch(api.Patients.getPatient({ id: patientId }));
  }, [patient, patientId, dispatch]);

  // Prefill form from appointment prop
  useEffect(() => {
    if (props.appointment && options.length > 0) {
      const apt = props.appointment;
      console.log(apt);

      if (apt.concern_id) {
        setValue('concern_id', apt.concern_id);
        const option = options.find(
          ({ concerns }: any) => !!(concerns || []).find(({ id }: any) => id === apt.concern_id),
        );
        if (option) setValue('option_id', (option as any).id);
      }

      if (apt.type) setValue('type', apt.type);
      if (apt.complaints) setValue('complaints', apt.complaints);
      if (apt.clinic_id)
        setValue('clinic_id', ClinicUtils.resolveForwardedClinicId(apt.clinic_id, clinics));
      if (apt.date) {
        setValue('date', apt.date);
        setValue('time', apt.date);
      }
      if (apt.address) setValue('address', apt.address);
    }
  }, [props.appointment, options]);

  useEffect(() => {
    if (concern_id) {
      let option: any;
      if (!option_id) {
        option = (options || []).find(
          ({ concerns }: any) => !!(concerns || []).find(({ id }: any) => id === concern_id),
        );
        if (option) setValue('option_id', option.id);
      } else {
        option = options.find(({ id }: any) => id === option_id) || {};
      }
      if (option) {
        let concern = (option.concerns || []).find(({ id }: any) => id === concern_id) || {};
        if (concern.delivery_options?.length === 1) {
          const deliveryData = concern.delivery_options[0];
          Object.keys(deliveryData).forEach((key) => {
            setValue(key as keyof AppointmentFormValues, deliveryData[key]);
          });
        }
      }
    }
  }, [options, concern_id]);

  const concern = useMemo(() => {
    return _.flatten(options.map(({ concerns = [] }: any) => concerns)).find(
      (c: any) => c.id === concern_id,
    );
  }, [options, concern_id]);

  useEffect(() => {
    if (!!prefillAddress && !address) {
      setAddress(prefillAddress);
    } else if (!prefillAddress && !address && !!patient) {
      setAddress(patient.address);
    }
  }, [prefillAddress, address, patient]);

  const filteredShifts = useMemo(() => {
    return CalendarUtils.filterShifts(shifts, momentDate, clinic_id);
  }, [shifts, momentDate, clinic_id]);

  let physicians = useMemo(() => {
    return AppointmentUtils.getAvailablePhysicians(
      allPhysicians,
      filteredShifts,
      clinic_id,
      patientId,
      concern,
    );
  }, [allPhysicians, filteredShifts, clinic_id, patientId, concern]);

  if (!date) {
    setTimeAndDate(moment());
  }

  function setTimeAndDate(momentVal: moment.Moment) {
    setValue('date', momentVal.unix());
    setValue('time', momentVal.unix());
  }

  function onAddressChange(event: React.ChangeEvent<HTMLInputElement>) {
    let newAddress = {
      ...address,
      [event.target.name]: event.target.value,
    };
    setAddress(newAddress);
  }

  function setAddress(addr: any) {
    setValue('address', addr);
  }

  function handleTypeChange(value: string) {
    let newAddress: any = undefined;

    if (value === APPOINTMENT_TYPES.clinic) {
      newAddress = ClinicUtils.getAddressAsObject(clinic);
    } else if (value === APPOINTMENT_TYPES.home) {
      newAddress = address || patient?.address;
    } else if (value === APPOINTMENT_TYPES.video) {
      let telemedicine_method = AppointmentUtils.getTelemedicineMethod().video;
      setValue('telemedicine_method', telemedicine_method);
    }

    setValue('type', value);
    setValue('selectedPhysician', undefined);

    if (newAddress) setValue('address', newAddress);
    else clearField('address');
  }

  function canSubmit() {
    return patientId && concern_id && selectedPhysician && time && date && type && clinic_id;
  }

  async function book(
    values: AppointmentFormValues,
    clickedConfirmPayment = false,
    clickedConfirmSlot = false,
    clickedSkipPayment = false,
  ) {
    let appointment = generateAppointmentDocument(values);

    if (confirmPayment) clickedConfirmPayment = confirmPayment;
    if (confirmSlot) clickedConfirmSlot = confirmSlot;

    setSubmitting(true);

    const response = await dispatch(
      api.Appointments.bookAppointment({
        appointment,
        userId: appointment.patient_id,
        confirmPayment: clickedConfirmPayment,
        confirmSlot: clickedConfirmSlot,
        skipPayment: clickedSkipPayment,
      }),
      {
        meta: true,
        title: 'Appointment Booking',
        successText: 'The appointment has been booked successfully!',
      },
    );

    setSubmitting(false);

    if (AppointmentUtils.responseNeedsConfirmation(response.status)) {
      if (response.status === 402) setConfirmType('payment');
      else if (response.status === 409) setConfirmType('slot');
      if (isResponseError(response.error)) {
        showConfirmationDialog(response.error?.response?.jsonBodyMessage);
      }
    }

    if (response.isSuccess) {
      if (values.confirmationMessage !== undefined && props.sendMessage !== undefined)
        props.sendMessage(values.confirmationMessage);

      setConfirmPayment(false);
      setConfirmType(undefined);
      reset();
      props.onClose();
    }
  }

  function changeConcern(concernId: string) {
    setValue('concern_id', concernId);
  }

  function onConfirm() {
    if (confirmType === 'payment') return onConfirmPayment();
    else if (confirmType === 'slot') return onConfirmSlot();
  }

  async function onSkipPayment() {
    setConfirmPayment(true);
    showConfirmationDialog(undefined);
    const values = getValues();
    await book(values, false, confirmSlot, true);
  }

  async function onConfirmPayment() {
    setConfirmPayment(true);
    showConfirmationDialog(undefined);
    const values = getValues();
    await book(values, true, confirmSlot, false);
  }

  async function onConfirmSlot() {
    setConfirmSlot(true);
    showConfirmationDialog(undefined);
    const values = getValues();
    await book(values, confirmPayment, true, false);
  }

  function onPatientSelected(selectedPatient: any) {
    setPatientId(selectedPatient ? selectedPatient.id : undefined);
    if (!selectedPatient) {
      selectPhysician(physician?.id);
    }
    setValue(
      'clinic_id',
      props.clinicId || selectedPatient?.clinic_id || physician?.clinic_id || null,
    );
  }

  function generateAppointmentDocument(values: AppointmentFormValues) {
    let { bookedBy } = props;

    let {
      date: valDate,
      time: valTime,
      type: valType,
      address: valAddress,
      complaints: valComplaints,
      concern_id: valConcernId,
      clinic_id: valClinicId,
      notes: valNotes = '',
      telemedicine_method: valTeleMethod = undefined,
    } = values;

    let computedDate = valDate;
    if (valDate && valTime) {
      let d = moment.unix(valDate);
      let t = moment.unix(valTime);
      d.hour(t.hour());
      d.minute(t.minute());
      computedDate = d.unix();
    }

    let appointment: any = {
      patient_id: patientId,
      booked_by: values.booked_by || (bookedBy !== undefined ? bookedBy.id : undefined),
      physician_id: selectedPhysician,
      date: computedDate,
      type: valType,
      address: valAddress,
      complaints: valComplaints,
      concern_id: valConcernId,
      clinic_id: valClinicId,
      notes: valNotes,
      telemedicine_method: valTeleMethod,
    };

    for (let key in appointment) {
      if (appointment[key] === null || appointment[key] === undefined) delete appointment[key];
    }

    if (appointment.type !== APPOINTMENT_TYPES.home) delete appointment.address;

    return appointment;
  }

  const isButtonEnabled = canSubmit() && !isSubmitting;

  const canBookTimeOff = StaffUtils.canBookTimeOff(authentication);

  return (
    <View
      allowClose={props.allowClose}
      onClose={props.onClose}
      sendMessage={props.sendMessage}
      bookedBy={props.bookedBy}
      physician={props.physician}
      startTime={props.startTime}
      prefillAddress={props.prefillAddress}
      appointment={props.appointment}
      fixedPatient={fixedPatient}
      patient={patient}
      physicians={physicians}
      shifts={filteredShifts}
      onConfirm={onConfirm}
      confirmType={confirmType}
      onConfirmSkipPayment={onSkipPayment}
      confirmationDialog={confirmationDialog}
      showConfirmationDialog={showConfirmationDialog}
      selectPhysician={selectPhysician}
      handleTypeChange={handleTypeChange}
      bookAppointment={handleSubmit((values) => book(values))}
      onAddressChange={onAddressChange}
      setAddress={setAddress}
      isButtonEnabled={isButtonEnabled}
      isSubmitting={isSubmitting}
      isCalendarVisible={isCalendarVisible}
      setCalendarVisible={setCalendarVisible}
      selectedPhysician={selectedPhysician}
      options={options}
      onPatientSelected={onPatientSelected}
      bookTimeOff={bookTimeOff}
      setBookTimeOff={canBookTimeOff ? setBookTimeOff : undefined}
      changeConcern={changeConcern}
      setTimeAndDate={setTimeAndDate}
      // RHF props passed to view
      control={control}
      setValue={setValue}
      watch={watch}
      // Watched form values passed as props for view sub-components
      option_id={option_id}
      address={address}
      complaints={complaints}
      date={date}
      time={time}
      type={type}
      telemedicine_method={telemedicine_method}
      concern_id={concern_id}
      clinic_id={clinic_id}
      notes={notes}
    />
  );
};

export default NewAppointment;
